/**
 * Calendrier d'amortissement visuel d'un scénario sur 25 ans.
 *
 * Affiche en SVG :
 *   - Courbe rouge : coût cumulé d'investissement (constant après l'achat)
 *   - Courbe verte : économies cumulées année après année
 *   - Point breakeven (croisement) marqué et chiffré
 *
 * Si le ROI > 25 ans, on étend l'horizon visuel jusqu'au breakeven
 * (max 50 ans). Si jamais rentabilisé (gain ≤ 0), on affiche un message
 * d'avertissement.
 */
export function AmortizationChart({
  netCostEur,
  annualSavingsEur,
  roiYears,
}: {
  netCostEur: number;
  annualSavingsEur: number;
  roiYears: number | null;
}) {
  if (annualSavingsEur <= 0 || netCostEur <= 0) {
    return (
      <div className="rounded border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600">
        Amortissement non représentable : les économies annuelles attendues
        sont nulles ou négatives.
      </div>
    );
  }

  const horizon = roiYears && roiYears > 25 ? Math.min(50, Math.ceil(roiYears + 5)) : 25;
  const width = 520;
  const height = 180;
  const padLeft = 50;
  const padRight = 20;
  const padTop = 20;
  const padBottom = 28;
  const chartW = width - padLeft - padRight;
  const chartH = height - padTop - padBottom;

  const maxYears = horizon;
  const maxEur = Math.max(netCostEur, annualSavingsEur * horizon) * 1.05;

  const x = (year: number) => padLeft + (year / maxYears) * chartW;
  const y = (eur: number) => padTop + (1 - eur / maxEur) * chartH;

  const costLine = `M ${x(0)} ${y(netCostEur)} L ${x(horizon)} ${y(netCostEur)}`;
  const savingsLine = `M ${x(0)} ${y(0)} L ${x(horizon)} ${y(annualSavingsEur * horizon)}`;

  // Axes
  const xTicks: number[] = [];
  for (let t = 0; t <= horizon; t += 5) xTicks.push(t);
  const yTicks: number[] = [];
  const yStep = Math.ceil(maxEur / 4 / 1000) * 1000;
  for (let v = 0; v <= maxEur; v += yStep) yTicks.push(v);

  return (
    <div className="rounded border border-gray-200 bg-white p-3">
      <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-700">
        Calendrier d'amortissement
      </h5>
      <svg viewBox={`0 0 ${width} ${height}`} className="mt-2 w-full">
        {/* Grille horizontale */}
        {yTicks.map((v) => (
          <g key={`y-${v}`}>
            <line
              x1={padLeft}
              x2={width - padRight}
              y1={y(v)}
              y2={y(v)}
              stroke="#e5e7eb"
              strokeDasharray="3 3"
            />
            <text x={padLeft - 5} y={y(v) + 3} fontSize="9" textAnchor="end" fill="#6b7280">
              {(v / 1000).toFixed(0)} k€
            </text>
          </g>
        ))}

        {/* Axe X */}
        <line x1={padLeft} x2={width - padRight} y1={height - padBottom} y2={height - padBottom} stroke="#9ca3af" />
        {xTicks.map((t) => (
          <g key={`x-${t}`}>
            <line x1={x(t)} x2={x(t)} y1={height - padBottom} y2={height - padBottom + 3} stroke="#9ca3af" />
            <text x={x(t)} y={height - padBottom + 14} fontSize="9" textAnchor="middle" fill="#6b7280">
              {t}{t === 0 ? "" : " an"}
            </text>
          </g>
        ))}

        {/* Courbe coût (rouge) */}
        <path d={costLine} fill="none" stroke="#ef4444" strokeWidth="2" />
        <text x={width - padRight} y={y(netCostEur) - 5} fontSize="10" textAnchor="end" fill="#ef4444">
          Reste à charge : {Math.round(netCostEur).toLocaleString("fr-FR")} €
        </text>

        {/* Courbe économies cumulées (vert) */}
        <path d={savingsLine} fill="none" stroke="#10b981" strokeWidth="2" />

        {/* Point breakeven */}
        {roiYears && roiYears <= horizon && (
          <g>
            <circle cx={x(roiYears)} cy={y(netCostEur)} r="5" fill="#10b981" />
            <line
              x1={x(roiYears)}
              x2={x(roiYears)}
              y1={y(netCostEur)}
              y2={height - padBottom}
              stroke="#10b981"
              strokeDasharray="2 2"
            />
            <text
              x={x(roiYears)}
              y={y(netCostEur) - 10}
              fontSize="11"
              textAnchor="middle"
              fill="#10b981"
              fontWeight="bold"
            >
              ⭐ Rentabilisé à {roiYears.toFixed(1)} ans
            </text>
          </g>
        )}
      </svg>

      <div className="mt-2 flex flex-wrap gap-4 text-xs">
        <div className="flex items-center gap-1">
          <span className="inline-block h-2 w-4 rounded" style={{ background: "#ef4444" }} />
          <span className="text-gray-700">Reste à charge (constant)</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="inline-block h-2 w-4 rounded" style={{ background: "#10b981" }} />
          <span className="text-gray-700">
            Économies cumulées (~{Math.round(annualSavingsEur).toLocaleString("fr-FR")} € / an)
          </span>
        </div>
      </div>
    </div>
  );
}
